import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    LogOut, RefreshCw, Users, Mail, MailOpen, FileText, MessageSquare, Star, CalendarCheck,
    BadgeCheck, Ban, Plus, Trash2, Phone, MapPin, DollarSign, Clock, StickyNote, Reply, X,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const STATUSES = ["NEW INQUIRY", "CONTACTED", "QUOTE SENT", "FOLLOW-UP", "INTERESTED", "BOOKED", "ACTIVE CLIENT", "NOT INTERESTED", "DO NOT CONTACT"];

const STATUS_COLORS = {
    "NEW INQUIRY": "bg-white/15 text-white border-white/40",
    "CONTACTED": "bg-white/10 text-zinc-300 border-white/25",
    "QUOTE SENT": "bg-gold/15 text-gold-light border-gold/40",
    "FOLLOW-UP": "bg-white/[0.07] text-zinc-400 border-white/15",
    "INTERESTED": "bg-white/20 text-white border-white/50",
    "BOOKED": "bg-white text-black border-white",
    "ACTIVE CLIENT": "bg-gold text-black border-gold",
    "NOT INTERESTED": "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
    "DO NOT CONTACT": "bg-zinc-800 text-zinc-500 border-zinc-700",
};

const formatErr = (detail) => {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return detail?.msg || String(detail);
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const Login = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            onLogin(data);
        } catch (err) {
            setError(formatErr(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-noir flex items-center justify-center px-4 led-dots">
            <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/12 bg-noir-elevated/90 backdrop-blur-xl p-8 shadow-[0_30px_90px_rgba(0,0,0,0.6)]" data-testid="admin-login-form">
                <p className="font-display text-2xl font-extrabold text-white text-center">WEALTHHAUS <span className="text-hotpink">MEDIA</span></p>
                <p className="mt-1 text-center font-alt text-[9px] font-bold tracking-[0.4em] text-zinc-500">SALES DASHBOARD</p>
                <label className="label-luxury mt-8">EMAIL</label>
                <input type="email" className="input-luxury" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@wealthhaus.net" required data-testid="admin-login-email" />
                <label className="label-luxury mt-4">PASSWORD</label>
                <input type="password" className="input-luxury" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="admin-login-password" />
                {error && <p className="mt-3 text-xs text-rose-400" role="alert" data-testid="admin-login-error">{error}</p>}
                <button type="submit" disabled={loading} className="btn-glossy-pink mt-6 w-full !py-3 disabled:opacity-60" data-testid="admin-login-submit">
                    {loading ? "SIGNING IN…" : "SIGN IN"}
                </button>
            </form>
        </div>
    );
};

const StatCard = ({ label, value, icon: Icon, testid }) => (
    <div className="rounded-xl border border-white/10 bg-noir-elevated p-4 sm:p-5" data-testid={testid}>
        <div className="flex items-center justify-between">
            <p className="font-alt text-[9px] sm:text-[10px] font-bold tracking-[0.2em] text-zinc-500">{label}</p>
            <Icon size={15} className="text-hotpink" />
        </div>
        <p className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-white" data-testid={`${testid}-value`}>{value ?? "—"}</p>
    </div>
);

const LeadDetail = ({ lead, onClose, onUpdate }) => {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const patch = async (body, successMsg) => {
        setBusy(true);
        try {
            await api.patch(`/admin/leads/${lead.id}`, body);
            toast.success(successMsg);
            onUpdate();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const rows = [
        ["Name", `${lead.first_name} ${lead.last_name}`],
        ["Business", lead.business_name],
        ["Business Type", lead.business_type],
        ["Phone", lead.phone],
        ["Email", lead.email],
        ["Location", lead.location],
        ["Campaign Start", lead.start_date],
        ["Campaign Length", lead.campaign_length],
        ["Budget", lead.budget],
        ["Advertising", lead.advertising_what],
        ["Artwork", `${lead.has_artwork ? "Has artwork" : "No artwork"}${lead.needs_design ? " · needs design help" : ""}`],
        ["Package Interest", lead.package_name || "—"],
        ["Email Consent", lead.consent_email ? "yes" : "no"],
        ["Source", lead.source || "website-funnel"],
        ["Inquiry", fmtDate(lead.created_at)],
        ["Payment", lead.payment_status === "paid" ? `PAID — $${(lead.paid_amount || 0).toLocaleString()}` : "Unpaid"],
        ["Sequence", lead.sequence_active ? "Follow-up sequence ACTIVE" : lead.sequence_completed ? "Sequence completed" : "Sequence stopped"],
    ];

    return (
        <div className="fixed inset-0 z-[60] flex justify-end bg-noir/70 backdrop-blur-sm" onClick={onClose} data-testid="lead-detail">
            <div className="h-full w-full max-w-xl overflow-y-auto bg-noir-surface border-l border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="font-display text-2xl font-extrabold text-white" data-testid="lead-detail-name">{lead.first_name} {lead.last_name}</h3>
                        <p className="text-sm text-zinc-400">{lead.business_name}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white" aria-label="Close" data-testid="lead-detail-close"><X size={20} /></button>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <select
                        value={lead.status}
                        disabled={busy}
                        onChange={(e) => patch({ status: e.target.value }, "Status updated")}
                        className="input-luxury !w-auto !py-2 !text-xs font-alt font-bold tracking-wider"
                        data-testid="lead-status-select"
                    >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {!lead.replied && (
                        <button onClick={() => patch({ replied: true }, "Marked as replied — automation stopped")} disabled={busy} className="btn-outline-gold !py-2 !px-4 !text-[10px]" data-testid="lead-mark-replied">
                            <Reply size={13} /> MARK AS REPLIED
                        </button>
                    )}
                    {lead.replied && <span className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-3 py-1.5 font-alt text-[10px] font-bold tracking-wider text-emerald-300">REPLIED — PERSONAL FOLLOW-UP</span>}
                </div>

                <div className="mt-6 rounded-xl border border-white/10 bg-noir/60 p-5">
                    <p className="font-alt text-[10px] font-bold tracking-[0.24em] text-hotpink mb-4">LEAD INFORMATION</p>
                    <dl className="space-y-2.5">
                        {rows.map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-4 text-[13px]">
                                <dt className="text-zinc-500 shrink-0">{k}</dt>
                                <dd className="text-right text-zinc-200 break-all">{v || "—"}</dd>
                            </div>
                        ))}
                    </dl>
                    {lead.message && (
                        <p className="mt-4 rounded-lg border border-white/10 bg-white/[0.04] p-3 text-[13px] text-zinc-300 leading-relaxed">“{lead.message}”</p>
                    )}
                    {(lead.artwork_files || []).length > 0 && (
                        <div className="mt-4" data-testid="lead-artwork">
                            <p className="font-alt text-[10px] font-bold tracking-[0.2em] text-zinc-500 mb-2">ARTWORK FILES</p>
                            <div className="grid grid-cols-3 gap-2">
                                {lead.artwork_files.map((p, i) => (
                                    <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-white/15 hover:border-white/40 transition-colors" data-testid={`lead-artwork-${i}`}>
                                        <img src={`${API}/files/${p}`} alt={`Artwork ${i + 1}`} className="h-20 w-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6">
                    <p className="font-alt text-[10px] font-bold tracking-[0.24em] text-hotpink mb-3 flex items-center gap-2"><StickyNote size={13} /> NOTES</p>
                    <div className="space-y-2.5">
                        {(lead.notes || []).map((n, i) => (
                            <div key={i} className="rounded-lg border border-white/10 bg-white/[0.04] p-3">
                                <p className="text-[13px] text-zinc-200">{n.text}</p>
                                <p className="mt-1 text-[10px] text-zinc-500">{fmtDate(n.at)} · {n.by}</p>
                            </div>
                        ))}
                        {(lead.notes || []).length === 0 && <p className="text-xs text-zinc-600">No notes yet.</p>}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <input className="input-luxury !py-2.5 !text-[13px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="lead-note-input" />
                        <button onClick={() => { if (note.trim()) { patch({ note }, "Note added"); setNote(""); } }} disabled={busy} className="btn-glossy-pink !py-2 !px-4 !text-[10px] shrink-0" data-testid="lead-note-add">
                            <Plus size={14} />
                        </button>
                    </div>
                </div>

                <div className="mt-6">
                    <p className="font-alt text-[10px] font-bold tracking-[0.24em] text-hotpink mb-3">COMMUNICATION HISTORY</p>
                    <div className="space-y-2.5" data-testid="lead-history">
                        {(lead.history || []).slice().reverse().map((h, i) => (
                            <div key={i} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                                {h.channel === "email" ? <Mail size={14} className="text-hotpink mt-0.5 shrink-0" /> : h.channel === "sms" ? <MessageSquare size={14} className="text-sky-300 mt-0.5 shrink-0" /> : <Clock size={14} className="text-zinc-500 mt-0.5 shrink-0" />}
                                <div className="min-w-0">
                                    <p className="text-[12px] font-semibold text-zinc-200 break-words">{h.summary}</p>
                                    <p className="mt-0.5 text-[10px] text-zinc-500">{h.kind} · {h.status} · {fmtDate(h.at)}</p>
                                </div>
                            </div>
                        ))}
                        {(lead.history || []).length === 0 && <p className="text-xs text-zinc-600">No communications yet.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TeamPanel = ({ user }) => {
    const [team, setTeam] = useState([]);
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/team");
            setTeam(data);
        } catch { /* non-admin */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (user.role !== "admin") return null;

    const add = async () => {
        if (!form.name.trim() || !form.email.trim() || !form.password) {
            toast.error("Name, email, and password are required.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/team", form);
            toast.success(`${form.email} added to the team`);
            setForm({ name: "", email: "", password: "" });
            load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/auth/team/${id}`);
            toast.success("Team member removed");
            load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        }
    };

    return (
        <div className="mt-10 rounded-2xl border border-white/10 bg-noir-elevated p-6 sm:p-8" data-testid="team-panel">
            <p className="font-alt text-[11px] font-bold tracking-[0.24em] text-hotpink flex items-center gap-2"><Users size={14} /> TEAM ACCESS — INVITE ONLY</p>
            <div className="mt-5 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3">
                <input className="input-luxury !py-2.5 !text-[13px]" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" />
                <input className="input-luxury !py-2.5 !text-[13px]" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" />
                <input className="input-luxury !py-2.5 !text-[13px]" placeholder="Temporary password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password" />
                <button onClick={add} disabled={busy} className="btn-glossy-pink !py-2.5 !px-5 !text-[10px]" data-testid="team-add">
                    <Plus size={14} /> INVITE
                </button>
            </div>
            <div className="mt-5 space-y-2">
                {team.map((m) => (
                    <div key={m.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`team-member-${m.email}`}>
                        <div>
                            <p className="text-sm font-semibold text-white">{m.name} {m.role === "admin" && <span className="ml-2 rounded-full bg-hotpink/15 border border-hotpink/40 px-2 py-0.5 font-alt text-[9px] font-bold tracking-wider text-hotpink">ADMIN</span>}</p>
                            <p className="text-xs text-zinc-500">{m.email}</p>
                        </div>
                        {m.role !== "admin" && (
                            <button onClick={() => remove(m.id)} className="p-2 text-zinc-500 hover:text-rose-400 transition-colors" aria-label={`Remove ${m.email}`} data-testid={`team-remove-${m.email}`}>
                                <Trash2 size={15} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const AdminApp = () => {
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l] = await Promise.all([api.get("/admin/stats"), api.get("/admin/leads")]);
            setStats(s.data);
            setLeads(l.data);
        } catch (err) {
            if (err.response?.status === 401) setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [user, loadData]);

    const openLead = async (id) => {
        try {
            const { data } = await api.get(`/admin/leads/${id}`);
            setSelected(data);
        } catch {
            toast.error("Could not load lead");
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        setUser(false);
    };

    if (user === null) return <div className="min-h-screen bg-noir" />;
    if (user === false) return (<><Login onLogin={setUser} /><Toaster position="top-center" richColors /></>);

    const cards = [
        { label: "TOTAL LEADS", value: stats?.total, icon: Users, testid: "stat-total" },
        { label: "NEW INQUIRIES", value: stats?.["NEW INQUIRY"], icon: MailOpen, testid: "stat-new" },
        { label: "QUOTES REQUESTED", value: stats?.total, icon: FileText, testid: "stat-quotes" },
        { label: "QUOTES SENT", value: stats?.["QUOTE SENT"], icon: Mail, testid: "stat-sent" },
        { label: "FOLLOW-UPS", value: stats?.["FOLLOW-UP"], icon: Reply, testid: "stat-followup" },
        { label: "INTERESTED LEADS", value: stats?.["INTERESTED"], icon: Star, testid: "stat-interested" },
        { label: "BOOKED CLIENTS", value: stats?.["BOOKED"], icon: CalendarCheck, testid: "stat-booked" },
        { label: "ACTIVE CLIENTS", value: stats?.["ACTIVE CLIENT"], icon: BadgeCheck, testid: "stat-active" },
    ];

    return (
        <div className="min-h-screen bg-noir text-white" data-testid="admin-dashboard">
            <Toaster position="top-center" richColors />
            <header className="sticky top-0 z-40 border-b border-white/10 bg-noir/85 backdrop-blur-xl">
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 flex h-16 items-center justify-between gap-4">
                    <p className="font-display text-lg font-extrabold">WEALTHHAUS <span className="text-hotpink">MEDIA</span> <span className="ml-2 font-alt text-[9px] font-bold tracking-[0.3em] text-zinc-500">SALES DASHBOARD</span></p>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:block text-xs text-zinc-500" data-testid="admin-user-email">{user.email}</span>
                        <button onClick={loadData} className="btn-chrome !py-2 !px-3.5 !text-[10px]" data-testid="admin-refresh">
                            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> REFRESH
                        </button>
                        <button onClick={logout} className="btn-chrome !py-2 !px-3.5 !text-[10px]" data-testid="admin-logout">
                            <LogOut size={13} /> LOGOUT
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {cards.map((c) => <StatCard key={c.label} {...c} />)}
                </div>

                <div className="mt-8 rounded-2xl border border-white/10 bg-noir-elevated overflow-hidden" data-testid="leads-table">
                    <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                        <p className="font-alt text-[11px] font-bold tracking-[0.24em] text-hotpink">LEADS — NEWEST FIRST</p>
                        <span className="font-alt text-[10px] tracking-wider text-zinc-500">{leads.length} total</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left min-w-[720px]">
                            <thead>
                                <tr className="border-b border-white/8">
                                    {["LEAD", "BUSINESS", "PHONE", "BUDGET", "STATUS", "RECEIVED"].map((h) => (
                                        <th key={h} className="px-5 py-3 font-alt text-[9px] font-bold tracking-[0.2em] text-zinc-500">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((l) => (
                                    <tr
                                        key={l.id}
                                        onClick={() => openLead(l.id)}
                                        className="border-b border-white/5 cursor-pointer hover:bg-white/[0.04] transition-colors"
                                        data-testid={`lead-row-${l.id}`}
                                    >
                                        <td className="px-5 py-3.5">
                                            <p className="text-sm font-semibold text-white">{l.first_name} {l.last_name}</p>
                                            <p className="text-[11px] text-zinc-500">{l.email}</p>
                                        </td>
                                        <td className="px-5 py-3.5 text-[13px] text-zinc-300">{l.business_name}</td>
                                        <td className="px-5 py-3.5 text-[13px] text-zinc-300">{l.phone}</td>
                                        <td className="px-5 py-3.5 text-[13px] text-zinc-300">{l.budget}</td>
                                        <td className="px-5 py-3.5">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-alt text-[9px] font-bold tracking-wider ${STATUS_COLORS[l.status] || STATUS_COLORS["NEW INQUIRY"]}`} data-testid={`lead-status-${l.id}`}>
                                                {l.status === "DO NOT CONTACT" && <Ban size={10} />}
                                                {l.status}
                                                {l.replied && <Reply size={10} />}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3.5 text-[12px] text-zinc-500">{fmtDate(l.created_at)}</td>
                                    </tr>
                                ))}
                                {leads.length === 0 && (
                                    <tr><td colSpan={6} className="px-5 py-14 text-center text-sm text-zinc-500" data-testid="leads-empty">No leads yet. New inquiries will appear here automatically.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <TeamPanel user={user} />
            </main>

            {selected && (
                <LeadDetail
                    lead={selected}
                    onClose={() => setSelected(null)}
                    onUpdate={() => { setSelected(null); loadData(); }}
                />
            )}
        </div>
    );
};
